<template>
  <div class="relative" v-on-click-outside="close">

    <button type="button" :disabled="isDisabled" @click="toggle" class="block text-left w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 sm:text-sm/6 disabled:bg-gray-200 disabled:hover:cursor-not-allowed">

      <template v-if="isLoading">
        <div class="text-gray-400 italic">Aan het laden...</div>
      </template>

      <template v-else-if="!isMultiple && selected[0]">
        <span class="font-semibold">{{ selected[0].label }}</span>
      </template>

      <template v-else-if="isMultiple && selected[0]">
        <div class="flex flex-wrap gap-1">
          <Badge :label="option.label" v-for="option in selected" is-closeable @close="deselect(option.value)" @click.stop/>
        </div>
      </template>

      <template v-else>
        <span class="text-gray-400">{{ placeholder }}</span>
      </template>

      <div class="ml-auto pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <Loader v-if="isLoading"/>
        <ChevronDownIcon class="h-5 w-5 text-gray-500 transition-transform" :class="{ '-rotate-180': isOpen }" v-else/>
      </div>
    </button>

    <FadeTransition>
      <div v-show="isOpen" class="absolute z-50 w-full mt-1" @click.stop>

        <div class="overflow-hidden rounded-lg border border-gray-300 bg-white shadow-2xl">

          <div class="flex border-b p-2 border-gray-300" v-if="isSearchable">
            <FormInput v-model="modelSearch" placeholder="Zoek op item..." ref="searchInput"/>
          </div>

          <div class="max-h-60 p-2 overflow-y-auto overflow-hidden" ref="options">
            <ListTransition is-back v-if="options.length">
              <div v-for="option in options" :key="option.label" @click="select(option.value)" class="relative select-none flex items-center gap-x-2 p-2 rounded-lg hover:bg-gray-100 cursor-pointer">
                <component :is="option.icon" class="size-6" v-if="option.icon"/>
                <img alt="" class="size-6 rounded" :src="option.image" v-else-if="option.image"/>

                <span class="text-sm leading-6 font-medium text-gray-900" v-html="option.label"/>

                <div class="flex items-center gap-x-2 text-gray-400 ml-auto" v-if="option.command || option.commandIcon">
                  <component :is="option.commandIcon" class="size-6" v-if="option.commandIcon"/>
                  <span class="italic" v-if="option.command">{{ option.command }}</span>
                </div>
              </div>
            </ListTransition>

            <span class="p-2 italic text-gray-400" v-if="!options.length && !isLoading">Er zijn geen items beschikbaar...</span>
          </div>

        </div>

      </div>
    </FadeTransition>

  </div>
</template>

<script setup lang="ts">
import { Badge } from '@component/Badge'
import { FormInput } from '@component/Form'
import { Loader } from '@component/Miscellaneous'
import type { ComponentPublicInstance } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import type { FormSelectOption } from '@/interfaces'
import { ChevronDownIcon } from '@heroicons/vue/24/solid'
import { ref, nextTick, watch, useTemplateRef, unref } from 'vue'
import { ListTransition, FadeTransition } from '@component/Transitions'

const isOpen = ref<boolean>(false);
const optionsRef = useTemplateRef<HTMLElement>("options");
const searchInputRef = useTemplateRef<ComponentPublicInstance<typeof FormInput>>("searchInput");

const modelSearch = defineModel<string>("searchQuery", {
  default: ""
});

const emit = defineEmits<{
  open: [];
  search: [string];
  select: [string|number|null];
  deselect: [string|number|null];
}>();

const { isLoading, isMultiple, isDisabled, options, selected } = defineProps<{
  readonly placeholder: string;
  readonly isLoading?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiple?: boolean;
  readonly isSearchable?: boolean;
  readonly options: FormSelectOption[];
  readonly selected: FormSelectOption[];
}>();

function deselect(id: string|number|null): void {
  emit("deselect", id);
}

function select(id: string|number|null): void {
  emit("select", id);

  modelSearch.value = "";

  !isMultiple && (isOpen.value = false);

  nextTick(() => unref(searchInputRef)?.$el.focus());
}

function toggle(): void {
  if (isDisabled) return;

  isOpen.value = !isOpen.value;
}

function close(): void {
  isOpen.value = false;
}

watch(modelSearch, searchQuery => emit("search", searchQuery));

watch(isOpen, isOpen => {
  if (!isOpen) return;

  nextTick(() => unref(searchInputRef)?.$el.focus());

  nextTick(() => {
    const options = unref(optionsRef);

    if (!options) return;

    options.scrollTo({ top: 0, behavior: "instant" });
  });

  emit("open");
});
</script>